import { FeaturedIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ActivationCodeInput } from '@backrs_ui/grow-ui/src/components/ActivationCodeInput/ActivationCodeInput';
import {
  SubscriptionPlan,
  useGetSubscriptionPlansQuery,
} from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';

import { Typography, Box, CircularProgress, Alert, Theme, SxProps, Button } from '@mui/material';

import { PlanCard } from './PlanCard';
import { PaymentPageProps } from '../SubscriptionPageContainer';

type SubscriptionPlansProps = Readonly<{
  currentPlan?: string;
  selectedPriceId?: string;
  onSelectPlan: (priceId: string) => void;
  onContinue: () => void;
  continueLoading?: boolean;
  loading?: boolean;
  subscriptionView: PaymentPageProps['subscriptionView'];
}>;

const STRINGS = appStrings.modules.subscription.subscriptionPlans;

// Synthetic free option — not a Stripe product. Selecting it skips checkout entirely.
export const BASIC_PLAN_ID = 'basic';

const BASIC_PLAN: SubscriptionPlan = {
  id: BASIC_PLAN_ID,
  productId: '',
  name: STRINGS.basicPlan.name,
  description: STRINGS.basicPlan.description,
  amount: 0,
  currency: 'usd',
  interval: 'month',
  intervalCount: 1,
  features: [],
  recommended: false,
  active: true,
  displayOrder: 999,
  images: [],
};

export const SubscriptionPlans = ({
  currentPlan,
  selectedPriceId,
  onSelectPlan,
  onContinue,
  continueLoading = false,
  loading = false,
  subscriptionView,
}: SubscriptionPlansProps) => {
  const { data: plansData, isLoading, error } = useGetSubscriptionPlansQuery(undefined);
  const showActivationInput = subscriptionView === 'PAYWALL';

  const plans = [
    ...(plansData?.plans ?? []),
    ...(subscriptionView === 'ONBOARDING' ? [BASIC_PLAN] : []),
  ];

  const getTitle = () => {
    if (subscriptionView === 'ONBOARDING') {
      return STRINGS.selectPlan;
    }

    return STRINGS.upgradeYourPlan;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{STRINGS.failedToLoadPlans}</Alert>;
  }

  if (plans.length === 0) {
    return <Alert severity="info">{STRINGS.noAvailablePlans}</Alert>;
  }

  return (
    <Box sx={containerSx}>
      {subscriptionView !== 'ONBOARDING' && (
        <Box sx={headerContainerSx}>
          <FeaturedIcon iconName="Tag01" size="xl" color="Colors/Foreground/fg-secondary" />
          <Box sx={questionTitleContainerStyle}>
            <Typography variant="display-sm-semibold">{getTitle()}</Typography>
            {plans.length > 1 && (
              <Typography
                variant="text-md-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              >
                {STRINGS.chooseBestPlan}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box sx={plansContainerSx}>
        {plans.map((plan) => {
          return (
            <Box key={plan.id} sx={planWrapperSx}>
              <PlanCard
                plan={plan}
                isSelected={currentPlan === plan.name || selectedPriceId === plan.id}
                onSelectPlan={onSelectPlan}
                loading={loading}
                subscriptionView={subscriptionView}
              />
            </Box>
          );
        })}
      </Box>

      <Button
        id="subscription-continue-button"
        hierarchy="Primary"
        size="lg"
        fullWidth
        onClick={onContinue}
        disabled={!selectedPriceId || continueLoading}
        icon={continueLoading ? 'Leading' : 'Default'}
        endIcon={
          continueLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : undefined
        }
      >
        {STRINGS.continue}
      </Button>

      {showActivationInput && <ActivationCodeInput idPrefix="subscription-activation-code" />}
    </Box>
  );
};

const plansContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  maxWidth: '1200px',
  margin: '0 auto',
  '@media (min-width: 768px)': {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});

const planWrapperSx: SxProps<Theme> = () => ({
  flex: '1 1 auto',
  minWidth: '300px',
  maxWidth: '500px',
  '@media (min-width: 768px)': {
    flex: '0 1 calc(50% - 24px)',
    minWidth: '350px',
  },
  '@media (min-width: 1200px)': {
    flex: '0 1 calc(33.333% - 24px)',
  },
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  minWidth: { sm: '360px' },
  maxWidth: { sm: '360px' },
  width: { xs: '90vw', sm: 'unset' },
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-4xl'],
  textAlign: 'center',
});

const questionTitleContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  textAlign: 'center',
});
